import React from 'react'
import { Button, Checkbox, Popconfirm, Select, Table } from 'antd'
import { DeleteFilled, PlusOutlined } from '@ant-design/icons'
import { ColumnsType } from 'antd/es/table'
import { vacanciesApi } from 'api/vacancies'
import { EVacancyStatus, IVacancy } from 'models/vacancies'
import { IMAGES_URL } from 'shared/constants/urls'
import { showServerError } from 'shared/utils/showServerError'
import { VacancyModal } from './VacancyModal'

const STATUS_OPTIONS = [
  { value: EVacancyStatus.PUBLISHED, label: 'Опубликована' },
  { value: EVacancyStatus.ARCHIVED, label: 'В архиве' }
]

const formatSalary = (salary: number) =>
  salary ? `${salary.toLocaleString('ru-RU')} ₽` : 'По договорённости'

export const VacancyList: React.FC = () => {
  const [vacancies, setVacancies] = React.useState<IVacancy[]>([])
  const [isLoading, setIsLoading] = React.useState(false)
  const [showArchived, setShowArchived] = React.useState(false)
  const [savingStatusId, setSavingStatusId] = React.useState<string | null>(null)
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  // null — создание, иначе редактируем эту вакансию
  const [editingVacancy, setEditingVacancy] = React.useState<IVacancy | null>(null)

  const getVacancies = async () => {
    try {
      setIsLoading(true)
      const response = await vacanciesApi.getVacancies()
      setVacancies(response.data)
    } catch (e) {
      showServerError(e)
    } finally {
      setIsLoading(false)
    }
  }

  React.useEffect(() => {
    getVacancies()
  }, [])

  const openModal = (vacancy: IVacancy | null) => {
    setEditingVacancy(vacancy)
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditingVacancy(null)
  }

  const handleStatusChange = async (id: string, status: EVacancyStatus) => {
    try {
      setSavingStatusId(id)
      await vacanciesApi.changeVacancyStatus(id, status)
      await getVacancies()
    } catch (e) {
      showServerError(e)
    } finally {
      setSavingStatusId(null)
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await vacanciesApi.deleteVacancy(id)
      await getVacancies()
    } catch (e) {
      showServerError(e)
    }
  }

  const displayedVacancies = showArchived
    ? vacancies
    : vacancies.filter((v) => v.status !== EVacancyStatus.ARCHIVED)

  const columns: ColumnsType<IVacancy> = [
    {
      title: 'Фото',
      dataIndex: 'photos',
      width: 100,
      render: (_, { photos }) => (
        <div
          style={{
            width: 80,
            height: 56,
            backgroundImage: `url(${IMAGES_URL}${photos[0]})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            borderRadius: 4
          }}
        />
      )
    },
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name'
    },
    {
      title: 'Зарплата',
      dataIndex: 'salary',
      key: 'salary',
      width: 170,
      render: (_, { salary }) => formatSalary(salary)
    },
    {
      title: 'Адрес',
      key: 'address',
      render: (_, { location }) => location.name
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      width: 170,
      // выбор статуса и удаление не должны открывать вакансию, как клик по строке
      render: (_, vacancy) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Select
            size='small'
            style={{ width: 150 }}
            value={vacancy.status}
            options={STATUS_OPTIONS}
            loading={savingStatusId === vacancy._id}
            disabled={savingStatusId === vacancy._id}
            onChange={(status) => handleStatusChange(vacancy._id, status)}
          />
        </div>
      )
    },
    {
      title: '',
      dataIndex: 'operation',
      width: 60,
      render: (_, vacancy) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Popconfirm title='Удалить вакансию безвозвратно?' onConfirm={() => handleDelete(vacancy._id)}>
            <Button type='primary' danger icon={<DeleteFilled />} size='small' />
          </Popconfirm>
        </div>
      )
    }
  ]

  return (
    <div style={{ paddingBottom: 80 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 16, marginBottom: 32 }}>
        <Checkbox checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)}>
          Показать архивные
        </Checkbox>
        <Button type='primary' size='large' icon={<PlusOutlined />} onClick={() => openModal(null)}>
          Добавить вакансию
        </Button>
      </div>
      <Table
        scroll={{ x: 750 }}
        pagination={false}
        columns={columns}
        dataSource={displayedVacancies}
        loading={isLoading}
        rowKey={(el) => el._id}
        rowClassName='tableRow'
        onRow={(vacancy) => ({ onClick: () => openModal(vacancy) })}
      />
      <VacancyModal
        open={isModalOpen}
        vacancy={editingVacancy}
        onClose={closeModal}
        onSaved={() => {
          closeModal()
          getVacancies()
        }}
      />
    </div>
  )
}
